import { MouseEvent, ReactElement, useState } from 'react';
import {
  AppBar,
  Box,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Toolbar,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import Logo from '../assets/logo.webp';
import { useAuth } from '../stores';
import Loader from './loader';
import sortArrayByProperty from '../utils/sort';

const NavbarLink = styled(Link)({
  color: 'white',
  textDecoration: 'none',
  '&:hover': {
    color: 'white',
    textDecoration: 'none',
  },
});

const LogoImage = styled('img')({
  maxWidth: 150,
});

const Navbar = observer((): ReactElement => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (auth && auth.loading) {
    return (
      <div className="loaderContainer">
        <div className="loadingInnerContainer">
          <Loader />
        </div>
      </div>
    );
  }

  const isAdminOrReseller = auth.isAdmin || auth.isReseller;

  return (
    <AppBar
      position="fixed"
      sx={{ mb: 2, zIndex: (theme) => theme.zIndex.drawer + 1 }}
      color="primary"
      enableColorOnDark
    >
      <Container maxWidth="xl">
        <Toolbar>
          <NavbarLink to="/" sx={{ mr: 1 }}>
            <LogoImage src={Logo} alt="logo" />
          </NavbarLink>
          {!!auth.user && (
            <>
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                {auth.isAdmin && (
                  <>
                    <NavbarLink to="/search">
                      <MenuItem>{t('navbar.search')}</MenuItem>
                    </NavbarLink>
                    <NavbarLink to="/passwordreset">
                      <MenuItem>{t('navbar.passwordReset')}</MenuItem>
                    </NavbarLink>
                    <NavbarLink to="/subscriptions">
                      <MenuItem>{t('navbar.subscriptions')}</MenuItem>
                    </NavbarLink>
                    <NavbarLink to="/resellers">
                      <MenuItem>{t('navbar.resellers')}</MenuItem>
                    </NavbarLink>
                  </>
                )}
                {isAdminOrReseller && (
                  <>
                    <NavbarLink to="/create">
                      <MenuItem>{t('navbar.createAccount')}</MenuItem>
                    </NavbarLink>
                    <NavbarLink to="/news">
                      <MenuItem>{t('navbar.news')}</MenuItem>
                    </NavbarLink>
                    {auth.isReseller && (
                      <NavbarLink to="/resellerProfile">
                        <MenuItem>{t('navbar.profile')}</MenuItem>
                      </NavbarLink>
                    )}
                  </>
                )}
              </Box>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                {sortArrayByProperty(auth.userCompanies, 'name')?.map((company) => (
                  <NavbarLink to={`/company/${company.key}`} key={company.key}>
                    <MenuItem onClick={handleClose}>{company.name}</MenuItem>
                  </NavbarLink>
                ))}
                <Divider />
                <MenuItem onClick={() => auth.logOut().then(() => navigate('/'))}>Logout</MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
});

export default Navbar;
